import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import type { OneCConnectionCheck, OneCProfile, OneCWebhookAttentionRetry, OneCWebhookDelivery, OneCWebhookSecret } from '../model/types'
import {
  decodeOneCConnectionCheck,
  decodeOneCProfile,
  decodeOneCWebhookAttentionRetry,
  decodeOneCWebhookDelivery,
  decodeOneCWebhookSecret,
} from './contracts'
import { ONEC_CONNECTION_URLS } from './urls'

/** Тег профиля базы: `<rid>:PROFILE`; сброс области rid перечитывает и его. */
export const ONEC_PROFILE_TAG = 'OneCProfile' as const

/** Тег доставки вебхука: `<rid>:WEBHOOK`; смена секрета и сброс области rid перечитывают его. */
export const ONEC_WEBHOOK_TAG = 'OneCWebhook' as const

export interface OneCResourceArgs {
  resourceId: string
}

export const onecConnectionApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_PROFILE_TAG, ONEC_WEBHOOK_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecProfile: build.query<OneCProfile, OneCResourceArgs>({
      query: ({ resourceId }) => ({ url: ONEC_CONNECTION_URLS.profile(resourceId), decode: decodeOneCProfile }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(ONEC_PROFILE_TAG, resourceId, 'PROFILE'),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * Проверка связи — по кнопке, не при открытии окна: каждый вызов идёт в 1С,
     * а неверный пароль 1С считает (CLAUDE.md §3). Поэтому мутация: не
     * кэшируется и не перезапрашивается сама. Повтор отказа служба не
     * отправляет в 1С — помнит его до смены учётных данных. Это GET, и transport
     * повторил бы его на сбое сети — `noRetry`: одно нажатие — одна попытка.
     */
    onecConnectionCheck: build.mutation<OneCConnectionCheck, OneCResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_CONNECTION_URLS.check(resourceId),
        decode: decodeOneCConnectionCheck,
        noRetry: true,
      }),
    }),
    /** Доставка уведомлений 1С: читает inbox нашей службы, в 1С не ходит. */
    onecWebhookDelivery: build.query<OneCWebhookDelivery, OneCResourceArgs>({
      query: ({ resourceId }) => ({ url: ONEC_CONNECTION_URLS.webhookDelivery(resourceId), decode: decodeOneCWebhookDelivery }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(ONEC_WEBHOOK_TAG, resourceId, 'WEBHOOK'),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * Смена секрета: один POST без повтора — повторное нажатие выпускает ещё один секрет и отзывает предыдущий,
     * поэтому решение о повторе принимает человек. Ответ с секретом хранит только экран, в кэше его нет (binding сбрасывает мутацию).
     */
    onecWebhookRotate: build.mutation<OneCWebhookSecret, OneCResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_CONNECTION_URLS.webhookRotate(resourceId),
        method: 'POST',
        decode: decodeOneCWebhookSecret,
        noRetry: true,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceTag(ONEC_WEBHOOK_TAG, resourceId, 'WEBHOOK')],
    }),
    /**
     * «Повторить» пакеты, требующие внимания: один POST без повтора транспортом — нажатие решает человек. После ответа
     * состояние доставки перечитывается (размер очереди внимания).
     */
    onecWebhookAttentionRetry: build.mutation<OneCWebhookAttentionRetry, OneCResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_CONNECTION_URLS.webhookAttentionRetry(resourceId),
        method: 'POST',
        decode: decodeOneCWebhookAttentionRetry,
        noRetry: true,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceTag(ONEC_WEBHOOK_TAG, resourceId, 'WEBHOOK')],
    }),
  }),
})

export const { onecProfile, onecConnectionCheck, onecWebhookDelivery, onecWebhookRotate, onecWebhookAttentionRetry } = onecConnectionApi.endpoints
